import { useState, useEffect, useCallback, useMemo } from "react";

// Components
import Table from "./Childs/Table";

// Common Components
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";

// Image for the add button
import plus from "../../../assets/icons/plus-solid.svg";

import FormCard from "../../Common/Childs/Structure/FormCard";
import Delete from "./Delete";
import Form from "./Childs/Form";
import Dropdown from "../../Common/Dropdown";
import { useGlobal } from "../../../context/GlobalContextProvider";

// Initial state for form data
const INITIAL_FORM_STATE = {
  layer_url: "",
  layer_preview_url: "",
  layer_preview_url_preview: "",
  layer_id: 0,
  order: 0,
};

/**
 * API service for layer choice operations
 */
const layerChoiceAPI = {
  getAll: (layerId) => fetch(`/api/layer/${layerId}/layerchoice`),
  create: (layerId, data) =>
    fetch(`/api/layer/${layerId}/layerchoice`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  update: (layerId, id, data) =>
    fetch(`/api/layer/${layerId}/layerchoice/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  delete: (layerId, id) =>
    fetch(`/api/layer/${layerId}/layerchoice/${id}`, {
      method: "DELETE",
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
};

/**
 * API service for pattern operations
 */
const patternAPI = {
  getByArtPack: (artPackId) => fetch(`/api/artpack/${artPackId}/pattern`),
};

/**
 * API service for layer operations
 */
const layerAPI = {
  getByPattern: (patternId) => fetch(`/api/pattern/${patternId}/layer`),
};

/**
 * API service for file operations
 */
const fileAPI = {
  getByName: (name) => fetch(`/api/file/name/${name}`),
};

const Index = () => {
  const { artPack, setSpinner } = useGlobal();

  const [layerChoices, setLayerChoices] = useState([]);

  // Pattern state
  const [patternList, setPatternList] = useState([]);
  const [selectedPatternId, setSelectedPatternId] = useState(0);
  const [selectedPatternName, setSelectedPatternName] = useState("");

  // Layer state
  const [layerList, setLayerList] = useState([]);
  const [selectedLayerId, setSelectedLayerId] = useState(0);
  const [selectedLayerName, setSelectedLayerName] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  /**
   * Fetch all patterns for the selected art pack
   */
  useEffect(() => {
    const fetchPatterns = async () => {
      try {
        const res = await patternAPI.getByArtPack(artPack?.art_pack_id);
        if (res.status === 200) {
          const json = await res.json();
          const normalizedData = json.map((el) => ({
            id: el.pattern_id,
            name: el.pattern_name,
          }));
          setPatternList(normalizedData);
          setSelectedPatternId(normalizedData[0]?.id || 0);
          setSelectedPatternName(normalizedData[0]?.name || "");
        }
      } catch (error) {
        console.error("Error fetching patterns:", error);
      }
    };

    if (artPack) {
      fetchPatterns();
    }
  }, [artPack]);

  /**
   * Fetch layers filtered by selected pattern ID
   */
  useEffect(() => {
    setLayerList([]);
    setSelectedLayerId(0);

    const fetchLayers = async () => {
      try {
        const res = await layerAPI.getByPattern(selectedPatternId);
        if (res.status === 200) {
          const json = await res.json();
          const normalizedData = json.map((el) => ({
            id: el.layer_id,
            name: el.layer_name,
          }));
          setLayerList(normalizedData);
          setSelectedLayerId(normalizedData[0]?.id || 0);
          setSelectedLayerName(normalizedData[0]?.name || "");
        }
      } catch (error) {
        console.error("Error fetching layers:", error);
      }
    };

    if (selectedPatternId) {
      fetchLayers();
    }
  }, [selectedPatternId]);

  /**
   * Convert image buffer to base64 data URL
   */
  const getImageDataUrl = useCallback(async (name) => {
    if (!name) {
      console.warn("No name provided for getImage.");
      return null;
    }

    try {
      const req = await fileAPI.getByName(name);
      if (!req.ok) {
        throw new Error(`Failed to fetch image. Status: ${req.status}`);
      }

      const data = await req.json();

      if (!data?.image?.data) {
        throw new Error("Invalid image data received.");
      }

      const uint8Array = new Uint8Array(data.image.data);
      let binaryString = "";
      for (let i = 0; i < uint8Array.length; i++) {
        binaryString += String.fromCharCode(uint8Array[i]);
      }

      return `data:${data.type};base64,${btoa(binaryString)}`;
    } catch (error) {
      console.error(`Error in getImage for ${name}:`, error.message);
      return null;
    }
  }, []);

  /**
   * Process image data for layer choices
   */
  const processImages = useCallback(async (result) => {
    try {
      const data = await Promise.all(
        result.map(async (el) => {
          const layerUrlPreview = el.arweave ?? (await getImageDataUrl(el.layer_preview_url));
          return {
            id: el.layer_choice_id,
            layer_choice_id: el.layer_choice_id,
            layer_url: el.layer_url,
            layer_preview_url: el.layer_preview_url,
            layer_preview_url_preview: layerUrlPreview,
            layer_id: el.layer_id,
            order: el.order,
            layer_name: el.layerData?.layer_name || "",
          };
        })
      );
      setLayerChoices(data);
    } catch (error) {
      console.error("Error processing images:", error.message);
    }
  }, [getImageDataUrl]);

  /**
   * Load table data based on selected layer ID
   */
  const loadTableData = useCallback(async () => {
    if (!selectedLayerId) return;

    setSpinner(true);
    try {
      const res = await layerChoiceAPI.getAll(selectedLayerId);
      if (res.status === 200) {
        const result = await res.json();
        await processImages(result);
      }
    } catch (error) {
      console.error("Error loading layer choices:", error);
    } finally {
      setSpinner(false);
    }
  }, [selectedLayerId, setSpinner, processImages]);

  /**
   * Get all layer choices when layer ID changes
   */
  useEffect(() => {
    setLayerChoices([]);
    loadTableData();
  }, [loadTableData]);

  /**
   * Create a new layer choice
   */
  const createItem = useCallback(async (data) => {
    setSpinner(true);
    try {
      await layerChoiceAPI.create(selectedLayerId, data);
      await loadTableData();
      onDiscard();
    } catch (error) {
      console.error("Error creating layer choice:", error);
    } finally {
      setSpinner(false);
    }
  }, [selectedLayerId, loadTableData, setSpinner]);

  /**
   * Update an existing layer choice
   */
  const updateItem = useCallback(async (data) => {
    try {
      await layerChoiceAPI.update(selectedLayerId, formData.id, data);
      await loadTableData();
      onDiscard();
    } catch (error) {
      console.error("Error updating layer choice:", error);
    }
  }, [selectedLayerId, formData.id, loadTableData]);

  /**
   * Delete a layer choice
   */
  const deleteItem = useCallback(async () => {
    try {
      await layerChoiceAPI.delete(selectedLayerId, formData.id);
      await loadTableData();
      onDiscard();
    } catch (error) {
      console.error("Error deleting layer choice:", error);
    }
  }, [selectedLayerId, formData.id, loadTableData]);

  /**
   * Show form for adding a new layer choice
   */
  const addNew = useCallback(() => {
    setShowForm(true);
    setFormData(INITIAL_FORM_STATE);
  }, []);

  /**
   * Show form for editing an existing layer choice
   */
  const onEdit = useCallback((data) => {
    setShowForm(true);
    setFormData(data);
  }, []);

  /**
   * Show delete confirmation
   */
  const onDelete = useCallback((data) => {
    setShowForm(false);
    setFormData(data);
  }, []);

  /**
   * Reset form and hide it
   */
  const onDiscard = useCallback(() => {
    setShowForm(false);
    setFormData(INITIAL_FORM_STATE);
  }, []);

  /**
   * Determine if art pack allows editing
   */
  const isEditingAllowed = useMemo(() => !artPack?.approved, [artPack?.approved]);

  /**
   * Handle pattern dropdown change
   */
  const handlePatternChange = useCallback((id) => {
    setSelectedPatternId(id);
    setSelectedPatternName(patternList.find((p) => p.id === id)?.name || "");
  }, [patternList]);

  /**
   * Handle layer dropdown change
   */
  const handleLayerChange = useCallback((id) => {
    setSelectedLayerId(id);
    setSelectedLayerName(layerList.find((l) => l.id === id)?.name || "");
  }, [layerList]);

  /**
   * Render the form if showForm is true
   */
  if (showForm) {
    return (
      <>
        <Title title="Choices" />
        <Description text="Set up the choices for each layer here." />
        <FormCard>
          <Form
            handleSubmit={formData?.id ? updateItem : createItem}
            formData={formData}
            onDiscard={onDiscard}
            typeCheck={formData?.id ? "updateItem" : "createItem"}
            layerId={selectedLayerId}
          />
        </FormCard>
      </>
    );
  }

  /**
   * Render the delete confirmation if item is marked for deletion
   */
  if (!showForm && formData?.id && formData?.delete) {
    return (
      <Delete
        onDiscard={onDiscard}
        handleSubmit={deleteItem}
        formData={formData}
      />
    );
  }

  /**
   * Main render
   */
  return (
    <>
      <Title title="Choices" />
      <Description text="Set up the choices for each layer here." />
      
      {/* Pattern Dropdown */}
      <div style={{ width: "30%", marginBottom: "2rem" }}>
        {selectedPatternName && (
          <Dropdown
            category="artist"
            categorySubType="layer"
            dropdownList={patternList}
            dropdownHeader={selectedPatternName}
            handleChange={handlePatternChange}
          />
        )}
      </div>

      {/* Layer Dropdown */}
      <div style={{ width: "30%", marginBottom: "2rem" }}>
        {layerList.length > 0 && (
          <Dropdown
            category="artist"
            categorySubType="layerchoice"
            dropdownList={layerList}
            dropdownHeader={selectedLayerName}
            handleChange={handleLayerChange}
          />
        )}
      </div>

      {/* Add Choice button */}
      {isEditingAllowed && selectedPatternId > 0 && (
        <div className="add-btn" onClick={addNew}>
          Add Choice
          <img src={plus} className="add-icon" alt="Add icon" />
        </div>
      )}

      {/* Layer choices table */}
      <Table
        data={layerChoices}
        onEdit={onEdit}
        onDelete={onDelete}
        isAllowedToEditOrDelete={isEditingAllowed}
      />
    </>
  );
};

export default Index;
